import moment from "moment-timezone";
import { useCallback, useMemo, useRef, useState, useEffect } from "react";
import { Button, Form, Overlay } from "react-bootstrap";
import Datetime from "react-datetime";
import { Filter, X } from "react-feather";

const FILTER_PREFIX = "$LSTSN=";

const MODE = {
  NOT_SEEN: "<",
  SEEN: ">",
};

const CHECK = {
  HOURS: "HR",
  DAYS: "DY",
  TODAY: "TD",
  YESTERDAY: "YD",
  WEEK: "WE",
  MONTH: "MO",
  SPECIFIC: "SD",
};

const LastSeenFilter = ({ column: { filterValue, setFilter } }) => {
  const targetRef = useRef(null);
  const [showOverlay, setShowOverlay] = useState(false);

  /* -------------------------
     Structured filter state
  --------------------------*/
  const parseFilter = useCallback(() => {
    if (!filterValue?.startsWith(FILTER_PREFIX))
      return {
        mode: "",
        check: "",
        hours: "",
        days: "",
        date: null,
      };

    const payload = filterValue.substring(FILTER_PREFIX.length);

    const mode = payload[0];
    const check = payload.substring(1, 3);
    const rest = payload.substring(3);

    const result = {
      mode,
      check,
      hours: "",
      days: "",
      date: null,
    };

    if (check === CHECK.HOURS) result.hours = rest;
    if (check === CHECK.DAYS) result.days = rest;

    if (check === CHECK.SPECIFIC && rest) {
      const m = moment(rest);
      if (m.isValid()) result.date = m;
    }

    return result;
  }, [filterValue]);

  const [state, setState] = useState(parseFilter);

  /* keep state in sync with external filter */
  useEffect(() => {
    setState(parseFilter());
  }, [parseFilter]);

  /* -------------------------
     Serialization
  --------------------------*/
  const commit = useCallback(
    (s) => {
      let value = "";

      if (s.check === CHECK.HOURS) value = s.hours;
      if (s.check === CHECK.DAYS) value = s.days;
      if (s.check === CHECK.SPECIFIC) value = s.date?.toISOString?.() ?? "";

      setFilter(`${FILTER_PREFIX}${s.mode}${s.check}${value}`);
    },
    [setFilter]
  );

  /* -------------------------
     Handlers
  --------------------------*/
  const update = (patch) => {
    setState((prev) => {
      const next = { ...prev, ...patch };

      if (!next.mode) next.mode = MODE.NOT_SEEN;
      if (!next.check) next.check = CHECK.HOURS;

      commit(next);
      return next;
    });
  };

  const handleNumeric = (name, v) => {
    if (v === "") v = "1";
    if (v.startsWith("-") || v.startsWith("0")) v = "1";
    update({ [name]: v });
  };

  const handleCheck = (check) => {
    let patch = { check };

    if (check === CHECK.HOURS) patch.hours = "1";
    if (check === CHECK.DAYS) patch.days = "1";
    if (check === CHECK.SPECIFIC) patch.date = moment();

    update(patch);
  };

  const handleDate = (m) => {
    if (moment.isMoment(m)) {
      update({ check: CHECK.SPECIFIC, date: m });
    }
  };

  /* -------------------------
     Button text
  --------------------------*/
  const buttonText = useMemo(() => {
    if (!state.mode) return <Filter size={"1.2em"} className="m-0 p-0 me-1" />;

    const prefix = state.mode === MODE.NOT_SEEN ? "Not Seen" : "Seen";

    switch (state.check) {
      case CHECK.HOURS:
        return `${prefix} ${state.mode === MODE.NOT_SEEN ? "for" : "within"} ${
          state.hours
        } hours`;

      case CHECK.DAYS:
        return `${prefix} ${state.mode === MODE.NOT_SEEN ? "for" : "within"} ${
          state.days
        } days`;

      case CHECK.TODAY:
        return `${prefix} Today`;

      case CHECK.YESTERDAY:
        return `${prefix} Since Yesterday`;

      case CHECK.WEEK:
        return `${prefix} This Week`;

      case CHECK.MONTH:
        return `${prefix} This Month`;

      case CHECK.SPECIFIC:
        return `${prefix} since ${state.date.format("DD/MM/YYYY")}`;

      default:
        return "";
    }
  }, [state]);

  /* -------------------------
     Render
  --------------------------*/
  return (
    <>
      <div className="d-flex flex-nowrap">
        <Button
          variant="light"
          size="sm"
          ref={targetRef}
          onClick={() => setShowOverlay((v) => !v)}
        >
          {buttonText}
        </Button>

        <Button variant="" className="p-0 m-0" onClick={() => setFilter("")}>
          <X />
        </Button>
      </div>

      <Overlay
        target={targetRef.current}
        show={showOverlay}
        placement="bottom-start"
        rootClose
        onHide={() => setShowOverlay(false)}
      >
        {({
          placement,
          arrowProps,
          popper,
          hasDoneInitialMeasure,
          show,
          ...overlayProps
        }) => (
          <div
            {...overlayProps}
            style={{
              ...overlayProps.style,
              backgroundColor: "var(--bs-body-bg)",
              border: "1px solid black",
              borderRadius: 3,
            }}
            className="p-2"
          >
            <Form.Check
              inline
              type="radio"
              label="Not Seen"
              checked={state.mode === MODE.NOT_SEEN}
              onChange={() => update({ mode: MODE.NOT_SEEN })}
            />
            <Form.Check
              inline
              type="radio"
              label="Seen"
              checked={state.mode === MODE.SEEN}
              onChange={() => update({ mode: MODE.SEEN })}
            />

            <hr />

            <div className="d-flex justify-content-start align-items-center">
              <Form.Check
                className="me-2"
                type="radio"
                label={state.mode === MODE.NOT_SEEN ? "for" : "within"}
                checked={state.check === CHECK.HOURS}
                onChange={() => handleCheck(CHECK.HOURS)}
              />
              <Form.Control
                className="me-2"
                style={{ width: "6em" }}
                type="number"
                size="sm"
                value={state.hours}
                onChange={(e) => handleNumeric("hours", e.target.value)}
                disabled={state.check !== CHECK.HOURS}
              />
              Hours
            </div>

            <div className="d-flex justify-content-start align-items-center">
              <Form.Check
                className="me-2"
                type="radio"
                label={state.mode === MODE.NOT_SEEN ? "for" : "within"}
                checked={state.check === CHECK.DAYS}
                onChange={() => handleCheck(CHECK.DAYS)}
              />
              <Form.Control
                className="me-2"
                style={{ width: "6em" }}
                type="number"
                size="sm"
                value={state.days}
                onChange={(e) => handleNumeric("days", e.target.value)}
                disabled={state.check !== CHECK.DAYS}
              />
              Days
            </div>

            <Form.Check
              type="radio"
              label="Today"
              checked={state.check === CHECK.TODAY}
              onChange={() => handleCheck(CHECK.TODAY)}
            />

            <Form.Check
              type="radio"
              label="Since Yesterday"
              checked={state.check === CHECK.YESTERDAY}
              onChange={() => handleCheck(CHECK.YESTERDAY)}
            />

            <Form.Check
              type="radio"
              label="This Week"
              checked={state.check === CHECK.WEEK}
              onChange={() => handleCheck(CHECK.WEEK)}
            />

            <Form.Check
              type="radio"
              label="This Month"
              checked={state.check === CHECK.MONTH}
              onChange={() => handleCheck(CHECK.MONTH)}
            />

            <Form.Check
              type="radio"
              label="Since Specific Date"
              checked={state.check === CHECK.SPECIFIC}
              onChange={() => handleCheck(CHECK.SPECIFIC)}
            />
            <Datetime
              input={false}
              open
              value={state.date}
              onChange={handleDate}
              closeOnSelect={false}
              timeFormat={false}
              locale="en-gb"
              utc
              isValidDate={(d) => d < new Date()}
            />
          </div>
        )}
      </Overlay>
    </>
  );
};

export default LastSeenFilter;
